import React, { useState, useEffect } from 'react';
import { VALID_ROADMAP_STATUS } from '../utils/chartUtils';

// Função utilitária para converter data para formato YYYY-MM-DD para input type="date"
function formatDateForInput(dateValue) {
  if (!dateValue && dateValue !== 0) {
    return '';
  }
  
  // Se já é uma string no formato YYYY-MM-DD, retorna como está
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return dateValue;
  }
  
  // Se é um número (serial Excel), converte
  if (typeof dateValue === 'number' && dateValue > 0) {
    // Converte serial Excel para data JavaScript
    const excelEpoch = new Date(Date.UTC(1899, 11, 30)); // 30 de dezembro de 1899 (UTC)
    const days = Math.floor(dateValue);
    const ms = days * 24 * 60 * 60 * 1000;
    const resultDate = new Date(excelEpoch.getTime() + ms);
    
    const year = resultDate.getUTCFullYear();
    const month = String(resultDate.getUTCMonth() + 1).padStart(2, '0');
    const day = String(resultDate.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
  // Se é uma data no formato DD/MM/YYYY, converte
  if (typeof dateValue === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateValue)) {
    const [day, month, year] = dateValue.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  
  // Se é um objeto Date
  if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
    const year = dateValue.getUTCFullYear();
    const month = String(dateValue.getUTCMonth() + 1).padStart(2, '0');
    const day = String(dateValue.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
  // Se é string vazia ou traço, retorna vazio
  if (typeof dateValue === 'string' && (dateValue.trim() === '' || dateValue.trim() === '-')) {
    return '';
  }
  
  // Tenta criar uma data a partir da string
  if (typeof dateValue === 'string') {
    const parsedDate = new Date(dateValue);
    if (!isNaN(parsedDate.getTime())) {
      const year = parsedDate.getUTCFullYear();
      const month = String(parsedDate.getUTCMonth() + 1).padStart(2, '0');
      const day = String(parsedDate.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  }
  
  return '';
}

function RoadmapItemEditor({ item, onSave, onCancel, isEditing = false, parentItems = [], isSubitem = false }) {
  const [editedItem, setEditedItem] = useState(() => {
    if (item) {
      return {
        ...item,
        'Data de Inicio': formatDateForInput(item['Data de Inicio']),
        'Previsão de Entrega': formatDateForInput(item['Previsão de Entrega']),
        'Data de Conclusão': formatDateForInput(item['Data de Conclusão'])
      };
    }
    return {
      Nome: '',
      Status: VALID_ROADMAP_STATUS[0], // 'Planejado'
      'Data de Inicio': '',
      'Previsão de Entrega': '',
      'Data de Conclusão': '',
      parentId: item?.parentId || (isSubitem ? null : null),
      level: item?.level ?? (isSubitem ? 1 : 0)
    };
  });

  const [validationErrors, setValidationErrors] = useState([]);

  // Atualiza o estado interno quando as props mudam
  useEffect(() => {
    // Limpar erros de validação sempre que as props mudarem
    setValidationErrors([]);
    
    const normalizedItem = item ? {
      ...item,
      'Data de Inicio': formatDateForInput(item['Data de Inicio']),
      'Previsão de Entrega': formatDateForInput(item['Previsão de Entrega']),
      'Data de Conclusão': formatDateForInput(item['Data de Conclusão'])
    } : {
      Nome: '',
      Status: VALID_ROADMAP_STATUS[0], // 'Planejado'
      'Data de Inicio': '',
      'Previsão de Entrega': '',
      'Data de Conclusão': '',
      parentId: item?.parentId || (isSubitem ? null : null),
      level: item?.level ?? (isSubitem ? 1 : 0)
    };
    
    setEditedItem(normalizedItem);
  }, [item, isSubitem]);

  const statusOptions = VALID_ROADMAP_STATUS;

  // Função para validar o item
  const validateItem = (itemToValidate) => {
    const errors = [];
    
    // Validação para status "Concluído"
    if (itemToValidate.Status === 'Concluído') {
      // Verificar se todas as três datas estão preenchidas
      if (!itemToValidate['Data de Inicio']) {
        errors.push('Data de Início é obrigatória para itens concluídos.');
      }
      if (!itemToValidate['Previsão de Entrega']) {
        errors.push('Previsão de Entrega é obrigatória para itens concluídos.');
      }
      if (!itemToValidate['Data de Conclusão']) {
        errors.push('Data de Conclusão é obrigatória para itens concluídos.');
      }
      
      // Verificar se a data de conclusão é maior ou igual à data de início
      if (itemToValidate['Data de Inicio'] && itemToValidate['Data de Conclusão']) {
        const dataInicio = new Date(itemToValidate['Data de Inicio']);
        const dataConclusao = new Date(itemToValidate['Data de Conclusão']);
        
        if (dataConclusao < dataInicio) {
          errors.push('Data de Conclusão deve ser maior ou igual à Data de Início.');
        }
      }
    }
    
    return errors;
  };

  const handleSave = () => {
    if (editedItem.Nome.trim()) {
      // Validar o item antes de salvar
      const errors = validateItem(editedItem);
      
      if (errors.length > 0) {
        setValidationErrors(errors);
        return;
      }
      
      // Limpar erros se a validação passou
      setValidationErrors([]);
      
      const itemToSave = { 
        ...editedItem, 
        'Data de Conclusão': editedItem['Data de Conclusão'] || '',
        id: item?.id || Date.now(),
        parentId: editedItem.parentId,
        level: editedItem.level
      };
      
      onSave(itemToSave);
    }
  };

  const handleInputChange = (field, value) => {
    // Limpar erros de validação quando o usuário fizer alterações
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
    
    // Se o status mudou de "Concluído" para outro, limpa a data de conclusão
    if (field === 'Status' && editedItem.Status === 'Concluído' && value !== 'Concluído') {
      setEditedItem(prev => ({ 
        ...prev, 
        [field]: value,
        'Data de Conclusão': '' 
      }));
    } else {
      setEditedItem(prev => ({ ...prev, [field]: value }));
    }
  };

  return (
    <div className="p-3 bg-white border border-gray-200 rounded-lg">
      {isSubitem && parentItems.length > 0 && (
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-500 mb-1">Item pai</label>
          <select
            value={editedItem.parentId || ''}
            onChange={(e) => {
              handleInputChange('parentId', e.target.value === '' ? null : e.target.value);
            }}
            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">Selecione o item pai</option>
            {parentItems.map(parent => (
              <option key={parent.id} value={parent.id}>{parent.Nome}</option>
            ))}
          </select>
        </div>
      )}
      
      {/* Mensagens de validação */}
      {validationErrors.length > 0 && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start">
            <svg className="w-4 h-4 text-red-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div>
              <h4 className="text-sm font-medium text-red-800">Corrija os seguintes erros:</h4>
              <ul className="mt-1 text-sm text-red-700 list-disc list-inside">
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            {isSubitem ? 'Nome do subitem' : 'Nome do item'}
          </label>
          <input
            type="text"
            value={editedItem.Nome}
            onChange={(e) => handleInputChange('Nome', e.target.value)}
            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder={isSubitem ? "Nome do subitem" : "Nome do item"}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
          <select
            value={editedItem.Status}
            onChange={(e) => handleInputChange('Status', e.target.value)}
            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {statusOptions.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Data de Início
            {editedItem.Status === 'Concluído' && <span className="text-red-500 ml-1">*</span>}
          </label>
          <input
            type="date"
            value={editedItem['Data de Inicio']}
            onChange={(e) => handleInputChange('Data de Inicio', e.target.value)}
            className={`w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
              editedItem.Status === 'Concluído' && !editedItem['Data de Inicio'] 
                ? 'border-red-300 bg-red-50' 
                : 'border-gray-300'
            }`}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Previsão de Entrega
            {editedItem.Status === 'Concluído' && <span className="text-red-500 ml-1">*</span>}
          </label>
          <input
            type="date"
            value={editedItem['Previsão de Entrega']}
            onChange={(e) => handleInputChange('Previsão de Entrega', e.target.value)}
            className={`w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
              editedItem.Status === 'Concluído' && !editedItem['Previsão de Entrega'] 
                ? 'border-red-300 bg-red-50' 
                : 'border-gray-300'
            }`}
          />
        </div>
      </div>
      
      {/* Campo Data de Conclusão - aparece apenas quando status é "Concluído" */}
      {editedItem.Status === 'Concluído' && (
        <div className="mt-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Data de Conclusão
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="date"
                value={editedItem['Data de Conclusão']}
                onChange={(e) => handleInputChange('Data de Conclusão', e.target.value)}
                className={`w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                  !editedItem['Data de Conclusão'] 
                    ? 'border-red-300 bg-red-50' 
                    : 'border-gray-300'
                }`}
              />
            </div>
          </div>
        </div>
      )}
      <div className="flex gap-2 justify-end mt-3">
        <button
          onClick={handleSave}
          disabled={!editedItem.Nome.trim() || (isSubitem && !editedItem.parentId)}
          className="px-3 py-1 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          title={!editedItem.Nome.trim() ? 'Nome é obrigatório' : (isSubitem && !editedItem.parentId) ? 'Item pai é obrigatório para subitens' : ''}
        >
          {isEditing ? 'Salvar' : 'Adicionar'}
        </button>
        <button
          onClick={onCancel}
          className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm font-medium hover:bg-gray-300 transition-colors"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

export default RoadmapItemEditor;
